<template>
  <div class="job-status-multi-check">
    <kt-autoselect
      :mode="props.mode"
      :label="props.label"
      :options="statusOptions"
      display-by="Status"
      value-by="Id"
      :multiple="true"
      v-model="model"
      search-placeholder="Search status..."
      :empty-text="props.emptyText"
      :on-refresh="handleRefresh"
      @change="handleChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, type PropType } from 'vue';
import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import { useJobStatusStore } from '@/stores/JobStatusStore';
import type { JobStatusModel } from '@/models/JobStatusModel';

const props = defineProps({
  mode: {
    type: String,
    default: "dropdown"
  },
  label: { 
    type: String, 
    default: 'Select Status' 
  },
  filter: {
    type: Function as PropType<(status: JobStatusModel) => boolean>,
    default: null
  },
  emptyText: {
    type: String,
    default: 'No Matching Statuses'
  }
});

const emit = defineEmits<{
  change: [value: number[]];
}>();

const model = defineModel<number[]>({
  default: () => []
});

const statusStore = useJobStatusStore();

// Raw list from the store. `statusOptions` derives from it so the filter
// prop is re-applied reactively whenever any reactive state it reads
// changes after initial mount.
const rawStatuses = ref<JobStatusModel[]>([]);

const statusOptions = computed<JobStatusModel[]>(() =>
  props.filter ? rawStatuses.value.filter(props.filter) : rawStatuses.value
);

// Load status options on mount
onMounted(async () => {
  await statusStore.load();
  // Convert Record to Array
  rawStatuses.value = Object.values(statusStore.list) || [];
});

const handleChange = (value: number[]) => {
  emit('change', value);
};

const handleRefresh = async () => {
  await statusStore.refresh();
  // Convert Record to Array
  rawStatuses.value = Object.values(statusStore.list) || [];
};
</script>
